import Portal from '../atoms/Portal';
import { IconButton } from '../atoms/Button';
import Badge from '../atoms/Badge';
import Spinner from '../atoms/Spinner';
import {
  ChevronDown,
  ChevronUp,
  X,
  CheckCircle2,
  AlertCircle,
  Clock,
  Hourglass,
  XCircle,
  Pause,
  Play,
  RotateCw,
} from 'lucide-react';

/**
 * TaskProgressWidget — Google Drive-Style Floating Background Task Progress Widget
 *
 * A generic, persistent floating widget for monitoring long-running background tasks
 * (WhatsApp broadcasts, CSV imports, PDF exports, batch data updates).
 *
 * Conforms strictly to GEMINI.md:
 * - Zero inline styling (`style={...}`).
 * - Built exclusively with @ui primitives.
 * - Supports full expanded inspection or compact bottom-right minimized pill mode.
 */
export default function TaskProgressWidget({
  isOpen = false,
  title = 'Memproses Tugas',
  isMinimized = false,
  onToggleMinimize,
  onClose,
  status = 'running', // 'running' | 'paused' | 'completed' | 'cancelled' | 'error'
  progress = { current: 0, total: 0 },
  stats = [],
  items = [],
  actions = null,
  footerNotice = null,
}) {
  if (!isOpen) return null;

  const current = Number(progress?.current ?? 0);
  const total = Number(progress?.total ?? 0);
  const percent =
    progress?.percent != null
      ? Math.round(progress.percent)
      : total > 0
        ? Math.min(100, Math.round((current / total) * 100))
        : 0;

  // Header status icon and modifier class
  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle2 size={16} />;
      case 'paused':
        return <Pause size={16} />;
      case 'cancelled':
      case 'error':
        return <AlertCircle size={16} />;
      case 'running':
      default:
        return <Spinner size="sm" />;
    }
  };

  const getStatusModifier = () => {
    switch (status) {
      case 'completed':
        return 'completed';
      case 'paused':
        return 'paused';
      case 'cancelled':
        return 'cancelled';
      case 'error':
        return 'error';
      case 'running':
      default:
        return 'running';
    }
  };

  const statusModifier = getStatusModifier();
  const progressBarClass = `task-progress-bar task-progress-bar--${statusModifier}`;

  return (
    // All open widgets share one fixed corner stack, so two background tasks never overlap
    <Portal containerId="task-progress-root" containerClassName="task-progress-stack">
      <div className="task-progress-portal">
        {isMinimized ? (
          /* ── Minimized Google Drive-Style Pill Bar ───────────────── */
          <div
            className="task-progress-minimized"
            onClick={onToggleMinimize}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onToggleMinimize?.();
              }
            }}
            title="Klik untuk membuka detail progres"
          >
            <div className={`task-progress-minimized__icon task-progress-minimized__icon--${statusModifier}`}>
              {getStatusIcon()}
            </div>

            <div className="task-progress-minimized__content">
              <div className="task-progress-minimized__title">
                {title}
              </div>
              <div className="task-progress-minimized__subtitle">
                {current} dari {total} ({percent}%)
              </div>
              <progress
                className={`${progressBarClass} task-progress-minimized__bar-mini`}
                value={current}
                max={total || 1}
              />
            </div>

            <div
              className="task-progress-minimized__actions"
              onClick={(e) => e.stopPropagation()}
            >
              {onToggleMinimize && (
                <IconButton
                  icon={ChevronUp}
                  size="sm"
                  variant="ghost"
                  title="Perluas Detail"
                  aria-label="Perluas Detail"
                  onClick={onToggleMinimize}
                />
              )}
              {onClose && (
                <IconButton
                  icon={X}
                  size="sm"
                  variant="ghost"
                  title="Tutup / Batalkan"
                  aria-label="Tutup / Batalkan"
                  onClick={onClose}
                />
              )}
            </div>
          </div>
        ) : (
          /* ── Expanded Full Card View ─────────────────────────────── */
          <div className="task-progress-card">
            {/* Header */}
            <div className="task-progress-header">
              <div className="task-progress-header__left">
                <div className={`task-progress-header__icon task-progress-header__icon--${statusModifier}`}>
                  {getStatusIcon()}
                </div>
                <div className="task-progress-header__title" title={title}>
                  {title}
                </div>
                {total > 0 && (
                  <span className="task-progress-header__count">
                    ({current}/{total})
                  </span>
                )}
              </div>

              <div className="task-progress-header__actions">
                {onToggleMinimize && (
                  <IconButton
                    icon={ChevronDown}
                    size="sm"
                    variant="ghost"
                    title="Minimalkan ke pojok"
                    aria-label="Minimalkan ke pojok"
                    onClick={onToggleMinimize}
                  />
                )}
                {onClose && (
                  <IconButton
                    icon={X}
                    size="sm"
                    variant="ghost"
                    title="Tutup"
                    aria-label="Tutup"
                    onClick={onClose}
                  />
                )}
              </div>
            </div>

            {/* Summary & Native Progress Bar */}
            <div className="task-progress-summary">
              <div className="task-progress-summary__meta">
                <div className="task-progress-summary__stats">
                  {stats.map((st, sIdx) => (
                    <span
                      key={sIdx}
                      className={`task-progress-summary__stat-badge task-progress-summary__stat-badge--${st.variant || 'neutral'}`}
                    >
                      {st.label}: {st.value}
                    </span>
                  ))}
                </div>
                <span className="task-progress-summary__percent">
                  {percent}%
                </span>
              </div>

              <progress
                className={progressBarClass}
                value={current}
                max={total || 1}
              />
            </div>

            {/* Action Bar (Pause / Resume / Cancel / Done) */}
            {actions && (
              <div className="task-progress-controls">
                {actions}
              </div>
            )}

            {/* Scrollable Live Itemized List */}
            {items && items.length > 0 && (
              <div className="task-progress-list">
                {items.map((it) => {
                  const itStatus = it.status || 'pending';
                  const isActive = itStatus === 'active';

                  return (
                    <div
                      key={it.id}
                      className={`task-progress-item ${isActive ? 'task-progress-item--active' : ''}`}
                    >
                      <div className={`task-progress-item__icon task-progress-item__icon--${itStatus}`}>
                        {itStatus === 'active' && <Spinner size="xs" />}
                        {itStatus === 'queued' && <Hourglass size={15} />}
                        {itStatus === 'success' && <CheckCircle2 size={15} />}
                        {itStatus === 'error' && <XCircle size={15} />}
                        {itStatus === 'pending' && <Clock size={15} />}
                      </div>

                      <div className="task-progress-item__details">
                        <div className="task-progress-item__title" title={it.title}>
                          {it.title}
                        </div>
                        {it.subtitle && (
                          <div className="task-progress-item__subtitle" title={it.subtitle}>
                            {it.subtitle}
                          </div>
                        )}
                        {it.error && (
                          <div className="task-progress-item__error" title={it.error}>
                            {it.error}
                          </div>
                        )}
                      </div>

                      {it.badge && (
                        <div className="task-progress-item__badge">
                          <Badge variant={it.badgeVariant || 'neutral'} size="sm">
                            {it.badge}
                          </Badge>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Optional Footer Notice */}
            {footerNotice && (
              <div className="task-progress-footer">
                {footerNotice}
              </div>
            )}
          </div>
        )}
      </div>
    </Portal>
  );
}

